import { type EnhancedScopeData } from '@ardoq/data-model';
import { APIEntityType, APITagAttributes, ArdoqId } from '@ardoq/api-types';
import {
  hexColorToRgbaObj,
  isValidHexColor,
  rgbaObjToStr,
  rgbaStringToObj,
} from '@ardoq/color-helpers';
import { logWarn } from '@ardoq/logging';

const toComparableColorFormat = (color: string | undefined) => {
  if (!color) return color;
  if (isValidHexColor(color)) {
    return rgbaObjToStr(hexColorToRgbaObj(color));
  }
  return rgbaObjToStr(rgbaStringToObj(color));
};

const getEditablePropertiesOfComponent = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) => {
  const targetComponent = scopeData.componentsById[componentId];
  const tags = scopeData.tags?.filter((tag: APITagAttributes) =>
    tag.components.includes(componentId)
  );
  const fields =
    scopeData.fieldsByModelIdAndTypeId[
      `${targetComponent.model}-${targetComponent.typeId}`
    ];
  return {
    component: {
      ...targetComponent,
      color: toComparableColorFormat(targetComponent.color),
    },
    tags,
    fields,
  };
};

const getEditablePropertiesOfReference = (
  scopeData: EnhancedScopeData,
  referenceId: ArdoqId
) => {
  const reference = scopeData.referencesById[referenceId];
  return {
    reference,
    tags: scopeData.tags?.filter((tag: APITagAttributes) =>
      tag.references.includes(referenceId)
    ),
    fields:
      scopeData.fieldsByModelIdAndTypeId[
        `${reference.model}-${reference.type}`
      ],
  };
};

export const getEditableEntityPropertiesToCompare = <T extends APIEntityType>(
  scopeData: EnhancedScopeData,
  entityType: T,
  entityID: ArdoqId
) => {
  switch (entityType) {
    case APIEntityType.COMPONENT:
      return getEditablePropertiesOfComponent(scopeData, entityID);
    case APIEntityType.REFERENCE:
      return getEditablePropertiesOfReference(scopeData, entityID);
    default:
      logWarn(
        Error(
          `Sidebar editor encountered unsupported APIEntityType - ${entityType}`
        ),
        null,
        {
          entityID,
          entityType,
        }
      );
      return undefined;
  }
};

export type EditableEntityPropertiesToCompare = ReturnType<
  typeof getEditableEntityPropertiesToCompare<APIEntityType>
>;
